import React from "react";

import VoucherStatusImg from "assets/images/bustle/vouchers/VoucherImage.png";
import classNames from "classnames";
import { DateTime } from "luxon";
import QRCode from "qrcode.react";
import { Trans, useTranslation } from "react-i18next";

import FlippableCard from "@common/Atoms/FlippableCard";

import { Button, Price } from "@onsite_app/MobileOrderingMenu/Atoms";
import { LogoImage } from "@onsite_app/MobileOrderingMenu/MainPage";

import { MinimalVoucher } from "./schema";

const VoucherQR = ({ voucher }: { voucher: MinimalVoucher }) => {
  return (
    <>
      <div className="relative">
        <div className="">
          <QRCode
            value={voucher.qrCode || "Missing QR Code"}
            size={112}
            bgColor="#ffffff"
            fgColor="#000000"
          />
        </div>
      </div>
    </>
  );
};
const VoucherCardFace = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="h-full rounded-venue-brand border-8 border-venue-primary bg-venue-tertiary p-24 py-12 shadow-sm">
      {children}
    </div>
  );
};

export const VoucherCardPreview = ({
  voucher,
}: {
  voucher: MinimalVoucher;
}) => {
  const [flipped, setIsFlipped] = React.useState(false);
  const { t } = useTranslation("onsite", { keyPrefix: "voucherClaim" });
  const toggleFlip = () => {
    setIsFlipped((f) => !f);
  };
  const hasMessage = voucher.giftMessage && !!voucher.giftMessage.trim();
  return (
    <>
      <div className="space-y-32">
        <div className="mx-auto w-[320px] max-w-xs">
          <FlippableCard
            height="550px"
            front={
              <VoucherCardFace>
                <div className="space-y-12 flex h-full flex-col items-stretch justify-center">
                  <div>
                    <img
                      src={VoucherStatusImg}
                      className={classNames(
                        "mx-auto",
                        hasMessage ? "h-128" : "h-196",
                      )}
                    />
                  </div>
                  <div className="h-full flex-1 flex-col justify-center">
                    <div className="space-y-24 flex-1 text-left">
                      <div className="overflow-hidden text-left text-xl font-semibold text-ellipsis whitespace-nowrap">
                        {voucher.recipientName}
                      </div>
                      <div className="flex-1 overflow-hidden overflow-y-auto whitespace-pre-line italic">
                        {voucher.giftMessage}
                      </div>
                      {voucher.senderName && (
                        <div className="overflow-x-clip text-ellipsis whitespace-nowrap">
                          <Trans
                            i18nKey="voucherClaim.from"
                            values={{ name: voucher.senderName }}
                            components={{
                              Name: <span className="text-xl font-semibold" />,
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <div>
                      <Button
                        type="button"
                        primary
                        onClick={(e) => {
                          e.preventDefault();

                          toggleFlip();
                        }}
                      >
                        {t("viewCode")}
                      </Button>
                    </div>
                  </div>
                </div>
              </VoucherCardFace>
            }
            back={
              <VoucherCardFace>
                <div className="space-y-24 flex h-full flex-col justify-between text-center">
                  <div className="space-y-12">
                    <div className="space-y-4">
                      <div className="mx-auto h-96 w-full max-w-[96px]">
                        <LogoImage />
                      </div>
                      <div className="max-w-full flex-1 text-ellipsis">
                        {voucher.venueName}
                      </div>
                    </div>
                    <div className="space-y-8">
                      <div className="custom-dashed" />
                      <div className="">
                        <div>
                          {voucher.voucherName && (
                            <>
                              {voucher.voucherName}
                              <br />
                            </>
                          )}
                        </div>
                        <div className="text-2xl font-semibold">
                          <Trans
                            i18nKey="voucherClaim.voucherValueName"
                            components={{
                              Value: (
                                <Price
                                  value={voucher.openingBalanceInCents}
                                  fromCents
                                  showCurrency
                                />
                              ),
                            }}
                          />
                        </div>
                        {voucher.expiresAt && (
                          <div className="text-xs">
                            <Trans
                              i18nKey="voucherClaim.expiresAt"
                              components={{
                                date: (
                                  <>
                                    {" "}
                                    {DateTime.fromISO(
                                      voucher.expiresAt,
                                    ).toLocaleString(DateTime.DATE_SHORT)}
                                  </>
                                ),
                              }}
                            />
                          </div>
                        )}
                      </div>
                      <div className="custom-dashed" />
                    </div>
                  </div>
                  <div className="space-y-12">
                    <div className="flex justify-center">
                      <div className="space-y-8">
                        <div className="rounded-venue-brand bg-white p-8">
                          <VoucherQR voucher={voucher} />
                        </div>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs">{t("code")}</div>
                      <div className="text-xl">{voucher.code} </div>
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <div>
                      <Button
                        type="button"
                        primary
                        onClick={(e) => {
                          e.preventDefault();
                          toggleFlip();
                        }}
                      >
                        {t("flipMeOver")}
                      </Button>
                    </div>
                  </div>
                </div>
              </VoucherCardFace>
            }
            flipped={flipped}
          />
        </div>
      </div>
    </>
  );
};
